import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, FileWarning, Save, ShieldAlert, ShieldCheck, ShieldOff } from "lucide-react";
import { api } from "@/lib/api";
import { dateLocale, t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import type { GenericResponse, LicenseStatus } from "@/lib/types";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(dateLocale(), { year: "numeric", month: "2-digit", day: "2-digit" });
}

const STATUS_META: Record<
  LicenseStatus["status"],
  { label: string; tone: "success" | "warning" | "destructive" | "muted"; icon: typeof ShieldCheck }
> = {
  full: { label: t("Aktiv"), tone: "success", icon: ShieldCheck },
  grace: { label: t("Grace-Period"), tone: "warning", icon: ShieldAlert },
  read_only: { label: t("Read-Only"), tone: "destructive", icon: ShieldOff },
  unlicensed: { label: t("Keine Lizenz"), tone: "destructive", icon: FileWarning },
};

export function LicensePage() {
  const [status, setStatus] = useState<LicenseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [paste, setPaste] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.get<LicenseStatus>("/api/license");
      setStatus(s);
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Lizenz-Status konnte nicht geladen werden") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onInstall = async (e: React.FormEvent) => {
    e.preventDefault();
    const license = paste.trim();
    if (!license) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      const resp = await api.post<GenericResponse>("/api/license/install", { license });
      if (resp.success) {
        setFeedback({ kind: "success", text: resp.message });
        setPaste("");
        await reload();
      } else {
        setFeedback({ kind: "error", text: resp.message });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Installation fehlgeschlagen") });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">{t("Lade Lizenz-Status…")}</div>;
  }

  const meta = status ? STATUS_META[status.status] : null;

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      {status && meta ? (
        <Card
          title={t("Lizenz-Status")}
          description={t("Bei Ablauf bleibt der Server in einer 14-tägigen Grace-Period funktionsfähig, danach wird der Ingest gesperrt.")}
          actions={<StatusBadge tone={meta.tone} label={meta.label} Icon={meta.icon} />}
        >
          {status.installed ? (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-xs">
              <Row label={t("Kunde")}>{status.customer ?? "—"}</Row>
              <Row label={t("Lizenz-ID")}><span className="font-mono">{status.license_id ?? "—"}</span></Row>
              <Row label={t("Ausgestellt")}>{formatDate(status.issued_at)}</Row>
              <Row label={t("Gültig bis")}>{formatDate(status.valid_until)}</Row>
              {status.days_remaining != null ? (
                <Row label={t("Tage bis Hard-Lock")}>
                  <span className="text-warning">{status.days_remaining}</span>
                </Row>
              ) : null}
              <Row label={t("Clients")}>
                {status.current_clients}{" "}
                {status.max_clients != null ? (
                  <span className="text-muted-foreground">/ {status.max_clients}</span>
                ) : null}
              </Row>
              <Row label={t("Features")}>
                {status.features.length === 0 ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {status.features.map((f) => (
                      <span
                        key={f}
                        className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[11px] text-accent-foreground"
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        {f}
                      </span>
                    ))}
                  </div>
                )}
              </Row>
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">
              Setup-Modus — der Server akzeptiert noch keine neuen Log-Einträge. Spiel hier deine Lizenz ein.
            </p>
          )}
        </Card>
      ) : null}

      <Card
        title={status?.installed ? t("Lizenz ersetzen") : t("Lizenz installieren")}
        description={
          status?.installed
            ? t("Verlängerung oder Erweiterung — JSON-Inhalt der neuen .lic-Datei einfügen.")
            : t("JSON-Inhalt der vom Vendor gelieferten .lic-Datei einfügen.")
        }
      >
        <form onSubmit={onInstall} className="flex flex-col gap-3">
          <textarea
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            rows={10}
            placeholder='{ "payload": { … }, "signature": "…" }'
            className="resize-y rounded-md border border-border bg-background p-3 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] text-muted-foreground">
              {t("Verlängerung gewünscht?")} <span className="font-medium">support@needful-apps.de</span>
            </p>
            <button
              type="submit"
              disabled={submitting || !paste.trim()}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
            >
              <Save className="h-3.5 w-3.5" /> {submitting ? t("Installiere…") : status?.installed ? t("Neue Lizenz übernehmen") : t("Lizenz installieren")}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}

function StatusBadge({
  tone,
  label,
  Icon,
}: {
  tone: "success" | "warning" | "destructive" | "muted";
  label: string;
  Icon: typeof ShieldCheck;
}) {
  const cls =
    tone === "success"
      ? "bg-success/15 text-success"
      : tone === "warning"
        ? "bg-warning/15 text-warning"
        : tone === "destructive"
          ? "bg-destructive/15 text-destructive"
          : "bg-muted text-muted-foreground";
  return (
    <span className={"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium " + cls}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}
